import React from 'react';
import { FC, ChangeEvent, MouseEvent, useState, useRef } from 'react';
import cx from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  DriftIcons,
} from 'drift-ui';
import styles from './UploadFiles.scss';
import { RootState } from 'app/configureStore';
import { useSelector } from 'react-redux';
import { getApplicationDataValue } from 'utils/utils';
import { useTranslation } from 'react-i18next';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import { useUpdateApplication } from 'utils/useUpdateApplication';
import { DO_APPLICATION_TYPE } from 'client-experience-toolbox';

const preventDefault = (event: MouseEvent<HTMLLabelElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

interface UploadFilesProps {
  error?: string;
  validator?: (localValue?) => boolean;
  secondaryText?: boolean;
  fileKey: string;
  documentType: string;
}

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'tiff', 'tif', 'ico', 'heic'];

const isValidFile = (file: File) => {
  if (!file) return false;
  const ext = file?.name?.split('.')?.pop()?.toLowerCase();
  return ext ? ALLOWED_EXTENSIONS.includes(ext) : false;
};

export const UploadFiles: FC<UploadFilesProps> = ({ error, validator, secondaryText, fileKey, documentType }) => {
  const application = useSelector((state: RootState) => state.applicationReducer.factoringApplication);
  const { AddCircleIcon } = DriftIcons;
  const labelRef = useRef<HTMLLabelElement>(null);
  const [showOutline, setShowOutline] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasUploadError, setHasUploadError] = useState(false);
  const [hasDeleteError, setHasDeleteError] = useState(false);
  const applicationData = application?.applicationData ?? {};
  const files = getApplicationDataValue(applicationData, fileKey);
  const isMaxFilesUploaded = files && files.length >= 3;
  const { patchApplicationPullData, deleteFileFromS3, getPresignedUrl, patchApplication, uploadDocumentToS3 } =
    useFormNetworkCalls(DO_APPLICATION_TYPE.FACTORING);
  const { t } = useTranslation('common', { keyPrefix: 'upload_documents_section' });
  const updateApplication = useUpdateApplication(DO_APPLICATION_TYPE.FACTORING);
  const [fileContentErrorMessage, setFileContentErrorMessage] = useState<string | null>(null);
  const uploadErrorMessage =
    documentType === 'driversLicenseOrPassport' ? t('upload_error_description_dl') : t('upload_error_description');

  const handleRemoveFile = async (file) => {
    const currentValue = files ?? [];
    if (currentValue.length > 0) {
      setIsDeleting(true);
      try {
        const updatedValue = currentValue.filter((f) => f.key !== file.key);
        try {
          await deleteFileFromS3(file.link);
        } catch {
          // Do nothing, we still want to update the application even if the file deletion fails
        }
        const pullDataKey = fileKey.includes('applicants[0]')
          ? fileKey.split('applicants[0]').join('applicants.0')
          : fileKey;
        await patchApplicationPullData(application._id, { [pullDataKey]: { key: file.key } });
        updateApplication(fileKey, updatedValue);
      } catch {
        setHasDeleteError(true);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleInvalidFile = () => {
    setFileContentErrorMessage(t('invalid_file_type_error'));
    setHasUploadError(true);
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { files: inputtedFiles },
    } = event;
    if (inputtedFiles && inputtedFiles.length > 0 && !isMaxFilesUploaded) {
      const file = inputtedFiles[0];

      if (!isValidFile(file)) {
        handleInvalidFile();
        return;
      }

      try {
        setIsUploading(true);
        const { presignedUrl, objectKey } = await getPresignedUrl(
          'factoringApplication',
          documentType,
          inputtedFiles[0].name,
        );
        await uploadDocumentToS3(inputtedFiles[0], presignedUrl);
        const currentValue = files ?? [];
        const updatedValue = [...currentValue, { key: uuidv4(), link: objectKey, display: inputtedFiles[0].name }];
        await patchApplication(fileKey, updatedValue);
        updateApplication(fileKey, updatedValue);
        validator && validator(updatedValue);
      } catch {
        setHasUploadError(true);
      } finally {
        setIsUploading(false);
      }
      event.target.value = '';
    }
  };

  const handleHighlight = (event: MouseEvent<HTMLLabelElement>) => {
    preventDefault(event);
    if (!isMaxFilesUploaded) setShowOutline(true);
  };

  const handleRemoveHighlight = (event: MouseEvent<HTMLLabelElement>) => {
    preventDefault(event);
    setShowOutline(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    handleRemoveHighlight(event);
    const {
      dataTransfer: { files },
    } = event;
    handleFileChange({ target: { files } } as ChangeEvent<HTMLInputElement>);
  };

  return (
    <div>
      <label
        ref={labelRef}
        htmlFor={fileKey}
        className={cx(
          styles.uploadButton,
          showOutline && styles.outline,
          error && styles.error,
          isMaxFilesUploaded && styles.disabled,
        )}
        onDragEnter={handleHighlight}
        onDragOver={handleHighlight}
        onDragLeave={handleRemoveHighlight}
        onDrop={handleDrop}
      >
        <AddCircleIcon color={isMaxFilesUploaded ? 'disabled' : 'primary'} />
        <div className={styles.uploadTextContainer}>
          {t('add_file_or_image')}
          {secondaryText && <p className={styles.dragAndDropText}>{t('drag_and_drop')}</p>}
        </div>
      </label>
      <input
        id={fileKey}
        type="file"
        accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
        hidden
        onChange={handleFileChange}
        disabled={isMaxFilesUploaded}
      />
      {error && (
        <p id={`fileError-${fileKey}`} className={styles.errorText}>
          {error}
        </p>
      )}
      <div>{files?.map((file) => <FileDisplay key={file.key} file={file} handleRemoveFile={handleRemoveFile} />)}</div>
      <FileLoadingScreen isLoading={isUploading} title={t('uploading_files')} />
      <FileLoadingScreen isLoading={isDeleting} title={t('deleting_file')} />
      <FileErrorScreen
        title={t('upload_error')}
        description={fileContentErrorMessage || uploadErrorMessage}
        hasError={hasUploadError}
        setHasError={setHasUploadError}
        setErrorText={setFileContentErrorMessage}
      />
      <FileErrorScreen
        title={t('file_delete_error')}
        description={t('file_delete_error_description')}
        hasError={hasDeleteError}
        setHasError={setHasDeleteError}
      />
    </div>
  );
};

interface FileDisplayProps {
  file: { key: string; link: string; display: string };
  handleRemoveFile: (file) => void;
}

const FileDisplay: FC<FileDisplayProps> = ({ file, handleRemoveFile }) => {
  const { CheckCircleIcon, DeleteForeverOutlinedIcon } = DriftIcons;
  return (
    <div className={styles.fileDisplay}>
      <div className={styles.fileInfo}>
        <CheckCircleIcon color="success" />
        <div>{file.display}</div>
      </div>
      <button onClick={() => handleRemoveFile(file)}>
        <DeleteForeverOutlinedIcon color="error" />
      </button>
    </div>
  );
};

type FileErrorScreenProps = {
  title: string;
  description: string;
  hasError: boolean;
  setHasError: (value: boolean) => void;
  setErrorText?: (text: string | null) => void;
};

const FileErrorScreen: FC<FileErrorScreenProps> = ({ title, description, hasError, setHasError, setErrorText }) => {
  const { CloseIcon } = DriftIcons;
  const { t: tCommon } = useTranslation();
  return (
    <Dialog open={hasError} onTransitionExited={() => setErrorText && setErrorText(null)}>
      <DialogTitle>{title}</DialogTitle>
      <div className={styles.errorCloseButton}>
        <IconButton
          onClick={() => setHasError(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'gray.700',
          }}
        >
          <CloseIcon />
        </IconButton>
      </div>
      <DialogContent className={styles.dialogContentContainer}>
        <div>{description}</div>
      </DialogContent>
      <div>
        <DialogActions>
          <Button variant="contained" onClick={() => setHasError(false)}>
            {tCommon('Close')}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
};

const FileLoadingScreen: FC<{ isLoading: boolean; title: string }> = ({ isLoading, title }) => {
  return (
    <Dialog open={isLoading}>
      <div className={styles.uploadingDialog}>
        <p className={styles.uploadingText}>{title}</p>
        <div className={styles.circularProgressContainer}>
          <CircularProgress className={styles.circularProgress} />
        </div>
      </div>
    </Dialog>
  );
};
